import { auth, provider } from './firebase';
import {
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    setPersistence,
    browserLocalPersistence,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    updateProfile,
    sendPasswordResetEmail,
    GoogleAuthProvider
} from 'firebase/auth';
import { getFirestore, getDoc, doc, collection, query, where, getDocs } from 'firebase/firestore';

// Set persistence once at module load (not awaited) so it doesn't break Safari's user-gesture chain on popup
setPersistence(auth, browserLocalPersistence).catch((e) => {
    console.error('Failed to set auth persistence:', e);
});

// Sync displayName from Firestore Users/{email} doc onto the auth user profile
const syncDisplayNameFromFirestore = async (user) => {
    if (!user) return user;
    try {
        const db = getFirestore();
        const docRef = doc(db, 'Users', user.email);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const userData = docSnap.data();
            if (userData.name && (!user.displayName || user.displayName !== userData.name)) {
                await updateProfile(user, { displayName: userData.name });
                console.log('User displayName updated to:', userData.name);
            }
        }
    } catch (updateError) {
        console.error('Error updating displayName:', updateError);
    }
    return user;
};

const saveGoogleAccessToken = (token) => {
    if (!token) return;

    const expiry = Date.now() + 3500 * 1000; // ~1 hour
    localStorage.setItem("g_access_token", token);
    localStorage.setItem("g_access_token_expiry", expiry.toString());
};

// Function to log in with Firebase and Google provider.
// Tries popup first; on Safari/popup-blocker environments, falls back to full-page redirect.
// When redirect is used, the function returns null and the caller should NOT setUser —
// onAuthStateChanged will pick up the signed-in user once the page returns.
export const loginWithGoogle = async () => {
    try {
        const result = await signInWithPopup(auth, provider);
        saveGoogleAccessToken(GoogleAuthProvider.credentialFromResult(result)?.accessToken);
        return await syncDisplayNameFromFirestore(result.user);
    } catch (error) {
        const fallbackCodes = [
            'auth/popup-blocked',
            'auth/popup-closed-by-user',
            'auth/cancelled-popup-request',
            'auth/operation-not-supported-in-this-environment',
        ];
        if (fallbackCodes.includes(error.code)) {
            await signInWithRedirect(auth, provider);
            return null;
        }
        throw new Error('Login error: ' + error.message);
    }
};

// Call once on app boot to handle the redirect-flow result (Safari fallback)
export const handleGoogleRedirectResult = async () => {
    try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
            saveGoogleAccessToken(GoogleAuthProvider.credentialFromResult(result)?.accessToken);
            await syncDisplayNameFromFirestore(result.user);
        }
    } catch (error) {
        console.error('Google redirect login error:', error);
    }
};

// Function to log in with email and password
export const loginWithEmailPassword = async (email, password) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        await syncDisplayNameFromFirestore(user);
        return user;
    } catch (error) {
        console.error('Email login error:', error);

        switch (error.code) {
            case 'auth/user-not-found':
                throw new Error('No account found with this email address');
            case 'auth/wrong-password':
                throw new Error('Incorrect password');
            case 'auth/invalid-email':
                throw new Error('Invalid email address');
            case 'auth/user-disabled':
                throw new Error('This account has been disabled');
            case 'auth/too-many-requests':
                throw new Error('Too many failed attempts. Please try again later');
            case 'auth/invalid-credential':
                throw new Error('Invalid email or password');
            default:
                throw new Error('Login failed. Please try again');
        }
    }
};

// Function to register with email and password
export const registerWithEmailPassword = async (email, password) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await signOut(auth);

        return user;
    } catch (error) {
        console.error('Registration error:', error);

        switch (error.code) {
            case 'auth/email-already-in-use':
                throw new Error('An account with this email already exists');
            case 'auth/invalid-email':
                throw new Error('Invalid email address');
            case 'auth/weak-password':
                throw new Error('Password should be at least 6 characters');
            case 'auth/operation-not-allowed':
                throw new Error('Email/password accounts are not enabled');
            default:
                throw new Error('Registration failed. Please try again');
        }
    }
};

// Function to log out
export const logout = async () => {
    try {
        await signOut(auth);
    } catch (error) {
        console.error('Logout error:', error);
        throw new Error('Logout failed');
    }
};

// Function to send password reset email
export const sendPasswordReset = async (email) => {
    try {
        await sendPasswordResetEmail(auth, email);
        return { success: true };
    } catch (error) {
        console.error('Password reset error:', error);

        switch (error.code) {
            case 'auth/user-not-found':
                throw new Error('No account found with this email address');
            case 'auth/invalid-email':
                throw new Error('Invalid email address');
            case 'auth/too-many-requests':
                throw new Error('Too many requests. Please try again later');
            default:
                throw new Error('Failed to send password reset email. Please try again');
        }
    }
};

// Function to check if the user is an admin
// Checks if the user's email appears in the 'admin' array of any course
export const checkIfAdmin = async (email) => {
    const db = getFirestore();

    console.log('[checkIfAdmin] Checking admin status for:', email);

    try {
        // Query the Courses collection for any course where this email is in the admin array
        const coursesCollection = collection(db, 'Courses');
        const q = query(coursesCollection, where('admin', 'array-contains', email));
        const querySnapshot = await getDocs(q);

        const isAdmin = !querySnapshot.empty;

        return isAdmin;
    } catch (error) {
        console.error('[checkIfAdmin] Error checking admin status:', error);
        return false;
    }
};
